import { computeFinancialHealth, computeValidationChecks } from '../../brandAssets';
import {
  COLOR,
  COMPANY,
  FONT,
  INR,
  PCT,
  SHEET,
  SIZE,
} from '../tokens';
import {
  applyPortraitPrint,
  border,
  clipSheetToColumns,
  estimateWrappedRowHeight,
  fill,
  formatGeneratedAt,
  formatPeriod,
  rangeRef,
  setWidths,
  writeExecutiveHeader,
  writePremiumMetaBand,
  writeSectionGap,
  writeSectionTitle,
} from '../layout';

const COLS = 4;
const WIDTHS = [30, 14, 32, 20];
const SECTION_GAP = 18;

function writeStatusBadge(sheet, row, startCol, endCol, label, value, tone) {
  const tones = {
    good: { fg: COLOR.success, bg: COLOR.successBg },
    warn: { fg: COLOR.warn, bg: COLOR.goldBg },
    bad: { fg: COLOR.danger, bg: COLOR.dangerBg },
  };
  const t = tones[tone] || tones.good;
  for (let c = startCol; c <= endCol; c += 1) {
    sheet.getCell(row, c).fill = fill(t.bg);
    sheet.getCell(row, c).border = border();
  }
  if (endCol > startCol) sheet.mergeCells(rangeRef(startCol, row, endCol, row));
  const cell = sheet.getCell(row, startCol);
  cell.value = `${label}: ${value}`;
  cell.font = { name: FONT, size: SIZE.body, bold: true, color: { argb: t.fg } };
  cell.fill = fill(t.bg);
  cell.border = border();
  cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: false, indent: 0 };
  sheet.getRow(row).height = 34;
}

function paintRowBand(sheet, row, fromCol, toCol, bg, borderColor = COLOR.border) {
  for (let c = fromCol; c <= toCol; c += 1) {
    sheet.getCell(row, c).fill = fill(bg);
    sheet.getCell(row, c).border = border(borderColor);
  }
}

function writeDashTableHeader(sheet, row, specs) {
  specs.forEach(({ from, to, label, align }) => {
    if (to > from) sheet.mergeCells(rangeRef(from, row, to, row));
    paintRowBand(sheet, row, from, to, COLOR.navy, COLOR.navyLight);
    const cell = sheet.getCell(row, from);
    cell.value = label;
    cell.font = { name: FONT, size: SIZE.tableHead, bold: true, color: { argb: COLOR.white } };
    cell.alignment = {
      vertical: 'middle',
      horizontal: align,
      indent: align === 'left' ? 1 : 0,
    };
  });
  sheet.getRow(row).height = 26;
}

function writeDashTableRow(sheet, row, cells, isAlt, rowHeight = 26) {
  const bg = isAlt ? COLOR.zebra : COLOR.white;
  cells.forEach(({ from, to, value, align, numFmt, color, bold, wrap }) => {
    if (to > from) sheet.mergeCells(rangeRef(from, row, to, row));
    paintRowBand(sheet, row, from, to, bg);
    const cell = sheet.getCell(row, from);
    cell.value = value;
    cell.font = {
      name: FONT,
      size: SIZE.body,
      bold: Boolean(bold),
      color: { argb: color || COLOR.navy },
    };
    cell.alignment = {
      vertical: wrap ? 'top' : 'middle',
      horizontal: align || 'left',
      indent: align === 'left' ? 1 : 0,
      wrapText: Boolean(wrap),
      shrinkToFit: false,
    };
    if (numFmt) cell.numFmt = numFmt;
  });
  sheet.getRow(row).height = rowHeight;
}

function writeDashTableTotal(sheet, row, cells) {
  cells.forEach(({ from, to, value, align, numFmt }) => {
    if (to > from) sheet.mergeCells(rangeRef(from, row, to, row));
    paintRowBand(sheet, row, from, to, COLOR.goldBg);
    const cell = sheet.getCell(row, from);
    cell.value = value;
    cell.font = { name: FONT, size: SIZE.body, bold: true, color: { argb: COLOR.navy } };
    cell.alignment = {
      vertical: 'middle',
      horizontal: align || 'left',
      indent: align === 'left' ? 1 : 0,
      shrinkToFit: false,
    };
    if (numFmt) cell.numFmt = numFmt;
  });
  sheet.getRow(row).height = 28;
}

function writeKpiHeader(sheet, row) {
  sheet.mergeCells(rangeRef(1, row, 2, row));
  sheet.mergeCells(rangeRef(3, row, COLS, row));
  const labelCell = sheet.getCell(row, 1);
  const valueCell = sheet.getCell(row, 3);
  labelCell.value = 'Metric';
  valueCell.value = 'Amount / Count';
  [labelCell, valueCell].forEach((cell, i) => {
    cell.font = { name: FONT, size: SIZE.tableHead, bold: true, color: { argb: COLOR.white } };
    cell.fill = fill(COLOR.navy);
    cell.border = border(COLOR.navyLight);
    cell.alignment = {
      vertical: 'middle',
      horizontal: i === 0 ? 'left' : 'right',
      indent: 1,
    };
  });
  sheet.getRow(row).height = 26;
}

function writeKpiRow(sheet, row, label, value, numFmt, accent, isAlt) {
  sheet.mergeCells(rangeRef(1, row, 2, row));
  sheet.mergeCells(rangeRef(3, row, COLS, row));
  const labelCell = sheet.getCell(row, 1);
  const valueCell = sheet.getCell(row, 3);
  labelCell.value = label;
  valueCell.value = value;
  const rowBg = isAlt ? COLOR.zebra : COLOR.white;
  labelCell.font = { name: FONT, size: SIZE.kpiLabel, bold: true, color: { argb: COLOR.muted } };
  labelCell.fill = fill(rowBg);
  labelCell.border = border();
  labelCell.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 };
  valueCell.font = { name: FONT, size: SIZE.kpiValue, bold: true, color: { argb: accent || COLOR.navy } };
  valueCell.fill = fill(rowBg);
  valueCell.border = border();
  valueCell.alignment = { horizontal: 'right', vertical: 'middle', indent: 1 };
  if (numFmt) valueCell.numFmt = numFmt;
  sheet.getRow(row).height = 28;
}

function writeChannelVolumeTable(sheet, startRow, channelRows, totalVol) {
  let row = startRow;
  writeDashTableHeader(sheet, row, [
    { from: 1, to: 2, label: 'Channel', align: 'left' },
    { from: 3, to: 3, label: 'Volume', align: 'right' },
    { from: 4, to: 4, label: 'Share %', align: 'center' },
  ]);
  row += 1;

  channelRows.forEach(([label, vol, share], i) => {
    const rowHeight = estimateWrappedRowHeight(label, WIDTHS[0] + WIDTHS[1], { minHeight: 28 });
    writeDashTableRow(sheet, row, [
      { from: 1, to: 2, value: label, align: 'left', wrap: true },
      { from: 3, to: 3, value: vol, align: 'right', numFmt: INR },
      { from: 4, to: 4, value: share, align: 'center', numFmt: PCT },
    ], i % 2 === 1, rowHeight);
    row += 1;
  });

  writeDashTableTotal(sheet, row, [
    { from: 1, to: 2, value: 'TOTAL', align: 'center' },
    { from: 3, to: 3, value: totalVol, align: 'right', numFmt: INR },
    { from: 4, to: 4, value: 1, align: 'center', numFmt: PCT },
  ]);
  return row;
}

function writeAuditChecksTable(sheet, startRow, auditRows) {
  let row = startRow;
  const detailsWidth = WIDTHS[2] + WIDTHS[3];
  writeDashTableHeader(sheet, row, [
    { from: 1, to: 1, label: 'Check', align: 'left' },
    { from: 2, to: 2, label: 'Result', align: 'center' },
    { from: 3, to: 4, label: 'Details', align: 'left' },
  ]);
  row += 1;

  auditRows.forEach(([name, result, detail], i) => {
    const resultColor = result === 'PASS' ? COLOR.success : COLOR.danger;
    const rowHeight = Math.max(
      estimateWrappedRowHeight(name, WIDTHS[0]),
      estimateWrappedRowHeight(detail, detailsWidth),
      32,
    );
    writeDashTableRow(sheet, row, [
      { from: 1, to: 1, value: name, align: 'left', wrap: true },
      { from: 2, to: 2, value: result, align: 'center', color: resultColor, bold: true },
      { from: 3, to: 4, value: detail, align: 'left', wrap: true },
    ], i % 2 === 1, rowHeight);
    row += 1;
  });
  return row;
}

export function buildExecutiveSummarySheet(workbook, ctx) {
  const { summary, analytics, meta, rows } = ctx;
  const health = computeFinancialHealth(summary, analytics);
  const validation = computeValidationChecks(summary, analytics, rows);

  const sheet = workbook.addWorksheet(SHEET.SUMMARY, {
    properties: { tabColor: { argb: COLOR.gold }, defaultRowHeight: 18 },
  });
  setWidths(sheet, WIDTHS);
  clipSheetToColumns(sheet, COLS);

  let row = 1;
  row = writeExecutiveHeader(
    sheet,
    row,
    COMPANY.name,
    'Financial Reconciliation Report',
    COLS,
  );

  writePremiumMetaBand(sheet, row, COLS,
    [
      { text: `Period: ${formatPeriod(meta.from, meta.to)}`, from: 1, to: 2 },
      { text: `Generated: ${formatGeneratedAt()}`, from: 3, to: COLS },
    ],
    [
      { text: `By: ${meta.generatedBy || COMPANY.module}`, from: 1, to: 2 },
      { text: `Report ID: ${meta.reportId || '—'}`, from: 3, to: 3 },
      { text: `${summary.count} transactions`, from: 4, to: COLS },
    ],
  );
  row += 2;
  writeSectionGap(sheet, row, COLS, { height: SECTION_GAP });
  row += 1;

  writeSectionTitle(sheet, row, 'Executive Dashboard', COLS);
  row += 1;
  writeStatusBadge(
    sheet, row, 1, 2,
    'Reconciliation Status', validation.status,
    validation.allPass ? 'good' : 'bad',
  );
  writeStatusBadge(
    sheet, row, 3, COLS,
    'Financial Health', `${health.label} (${health.score}/100)`,
    health.tone,
  );
  row += 1;
  writeSectionGap(sheet, row, COLS, { height: SECTION_GAP });
  row += 1;

  writeSectionTitle(sheet, row, 'Key Performance Indicators', COLS);
  row += 1;
  writeKpiHeader(sheet, row);
  row += 1;

  const kpis = [
    ['Total Credits', summary.totalCredits, INR, COLOR.credit],
    ['Total Debits', summary.totalDebits, INR, COLOR.debit],
    ['Net Balance', summary.netBalance, INR, COLOR.navy],
    ['Transaction Count', summary.count, null, COLOR.navy],
    ['GST Collected (18%)', analytics.totalGst, INR, COLOR.gold],
    ['Gross Volume', summary.grossVolume, INR, COLOR.navy],
  ];
  kpis.forEach(([label, val, fmt, accent], i) => {
    writeKpiRow(sheet, row, label, val, fmt, accent, i % 2 === 1);
    row += 1;
  });
  writeSectionGap(sheet, row, COLS, { height: SECTION_GAP });
  row += 1;

  writeSectionTitle(sheet, row, 'Payment Channel Volume', COLS);
  row += 1;

  const totalVol = analytics.channels.reduce((s, ch) => s + ch.credits + ch.debits, 0) || 1;
  const channelRows = analytics.channels.map((ch) => {
    const vol = ch.credits + ch.debits;
    return [ch.label, vol, vol / totalVol];
  });
  if (channelRows.length === 0) channelRows.push(['No data', 0, 0]);

  row = writeChannelVolumeTable(sheet, row, channelRows, totalVol);
  writeSectionGap(sheet, row, COLS, { height: SECTION_GAP });
  row += 1;

  writeSectionTitle(sheet, row, 'Audit Validation Checks', COLS);
  row += 1;
  const auditRows = validation.checks.map((c) => [c.name, c.pass ? 'PASS' : 'FAIL', c.detail]);
  row = writeAuditChecksTable(sheet, row, auditRows);
  writeSectionGap(sheet, row, COLS, { height: 12 });
  row += 1;

  setWidths(sheet, WIDTHS);
  clipSheetToColumns(sheet, COLS);
  applyPortraitPrint(sheet, {
    colCount: COLS,
    lastRow: row,
    printTitlesRow: '1:4',
    fitOnePage: false,
  });

  return sheet;
}
